import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router";
import { AuthContext } from "../../Context/AuthContext";
import { ProgressSpinner } from "primereact/progressspinner";

interface PrivateRouteProps {
  children: ReactNode;
  allowedRoles?: string[];
}

const REDIRECTING_TO_SITEMINDER_KEY = "redirectingToSiteminder";
const REDIRECTING_TO_SITEMINDER_AT_KEY = "redirectingToSiteminderAt";
const REDIRECT_FLAG_STALE_AFTER_MS = 30 * 1000;

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  allowedRoles = [],
}) => {
  const {
    isAuthenticated,
    hasActiveSession,
    signIn,
    authType,
    isUserValidated,
    isAuthorizedUser,
    appUser,
  } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [shouldRedirectToLoginError, setShouldRedirectToLoginError] =
    useState(false);
  const [shouldRedirectToLogin, setShouldRedirectToLogin] = useState(false);

  const location = useLocation();

  // Keep the path that caused this route guard to initialize.
  // Normal SPA navigation should not trigger another SiteMinder
  // session verification only because pathname changed.
  const initialPathRef = useRef(location.pathname);

  useEffect(() => {
    let isMounted = true;

    const clearRedirectingFlag = (reason: string) => {
      try {
        sessionStorage.removeItem(REDIRECTING_TO_SITEMINDER_KEY);
        sessionStorage.removeItem(REDIRECTING_TO_SITEMINDER_AT_KEY);

        console.log("[PrivateRoute] Cleared SiteMinder redirect flag.", {
          reason,
        });
      } catch (err) {
        console.warn(
          "[PrivateRoute] Failed to clear SiteMinder redirect flag.",
          err
        );
      }
    };

    const setRedirectingFlag = () => {
      try {
        sessionStorage.setItem(REDIRECTING_TO_SITEMINDER_KEY, "true");

        sessionStorage.setItem(
          REDIRECTING_TO_SITEMINDER_AT_KEY,
          String(Date.now())
        );

        console.log("[PrivateRoute] Set SiteMinder redirect flag.");
      } catch (err) {
        console.warn(
          "[PrivateRoute] Failed to set SiteMinder redirect flag.",
          err
        );
      }
    };

    const isRedirectingFlagStale = (): boolean => {
      try {
        const redirecting = sessionStorage.getItem(
          REDIRECTING_TO_SITEMINDER_KEY
        );

        const redirectingAtRaw = sessionStorage.getItem(
          REDIRECTING_TO_SITEMINDER_AT_KEY
        );

        if (redirecting !== "true") {
          return false;
        }

        if (!redirectingAtRaw) {
          console.warn(
            "[PrivateRoute] redirectingToSiteminder flag exists without timestamp. Treating as stale."
          );

          return true;
        }

        const redirectingAt = Number(redirectingAtRaw);

        if (!Number.isFinite(redirectingAt)) {
          console.warn(
            "[PrivateRoute] redirectingToSiteminder timestamp is invalid. Treating as stale."
          );

          return true;
        }

        const ageMs = Date.now() - redirectingAt;

        const stale =
          ageMs > REDIRECT_FLAG_STALE_AFTER_MS;

        console.log(
          "[PrivateRoute] redirectingToSiteminder flag check:",
          {
            redirecting,
            redirectingAt,
            redirectingAtTime:
              new Date(redirectingAt).toLocaleString(),
            ageMs,
            staleAfterMs: REDIRECT_FLAG_STALE_AFTER_MS,
            stale,
          }
        );

        return stale;
      } catch (err) {
        console.warn(
          "[PrivateRoute] Failed to evaluate redirect flag age. Treating as stale.",
          err
        );

        return true;
      }
    };

    const verify = async () => {
      try {
        const activePath = initialPathRef.current;

        const isCallback =
          activePath.includes("/signin-oidc-cwopa");

        const loginDenied =
          sessionStorage.getItem("loginDenied");

        const redirectingFlag =
          sessionStorage.getItem(
            REDIRECTING_TO_SITEMINDER_KEY
          );

        console.log(
          "[PrivateRoute] Verifying session..."
        );

        console.log(
          "[PrivateRoute] active path:",
          activePath
        );

        console.log(
          "[PrivateRoute] loginDenied flag:",
          loginDenied
        );

        console.log(
          "[PrivateRoute] redirecting flag:",
          redirectingFlag
        );

        setLoading(true);
        setShouldRedirectToLogin(false);
        setShouldRedirectToLoginError(false);

        const active = await hasActiveSession();

        if (!isMounted) {
          return;
        }

        if (active) {
          clearRedirectingFlag("active-session");
          setLoading(false);
          return;
        }

        if (authType === "siteminder") {
          if (loginDenied === "true") {
            console.warn(
              "[PrivateRoute] Login was previously denied. Redirecting to login-error."
            );

            clearRedirectingFlag(
              "login-denied"
            );

            setShouldRedirectToLoginError(true);
            setLoading(false);

            return;
          }

          if (isCallback) {
            console.warn(
              "[PrivateRoute] Callback route reached without active session. Stopping spinner."
            );

            clearRedirectingFlag(
              "callback-without-session"
            );

            setShouldRedirectToLogin(true);
            setLoading(false);

            return;
          }

          const alreadyRedirecting =
            sessionStorage.getItem(
              REDIRECTING_TO_SITEMINDER_KEY
            ) === "true";

          if (
            alreadyRedirecting &&
            isRedirectingFlagStale()
          ) {
            clearRedirectingFlag(
              "stale-redirect-flag"
            );
          }

          const shouldStartRedirect =
            sessionStorage.getItem(
              REDIRECTING_TO_SITEMINDER_KEY
            ) !== "true";

          if (shouldStartRedirect) {
            console.log(
              "[PrivateRoute] Redirecting to SiteMinder..."
            );

            setRedirectingFlag();

            await signIn();

            return;
          }

          console.warn(
            "[PrivateRoute] Redirect flag is still active. Redirecting user to /login instead of showing infinite spinner."
          );

          setShouldRedirectToLogin(true);
          setLoading(false);

          return;
        }

        setShouldRedirectToLogin(true);
        setLoading(false);
      } catch (err) {
        console.error(
          "[PrivateRoute] Session verification error:",
          err
        );

        if (isMounted) {
          setShouldRedirectToLogin(true);
          setLoading(false);
        }
      }
    };

    void verify();

    return () => {
      isMounted = false;
    };

    // Important:
    // Do not add location.pathname here.
    // Normal route navigation must not trigger a complete
    // SiteMinder session verification.
  }, [
    authType,
    hasActiveSession,
    signIn,
  ]);

  useEffect(() => {
    if (isAuthenticated) {
      try {
        sessionStorage.removeItem(
          REDIRECTING_TO_SITEMINDER_KEY
        );

        sessionStorage.removeItem(
          REDIRECTING_TO_SITEMINDER_AT_KEY
        );

        console.log(
          "[PrivateRoute] Removed SiteMinder redirect flag after authentication."
        );
      } catch (err) {
        console.warn(
          "[PrivateRoute] Failed removing SiteMinder redirect flag after authentication.",
          err
        );
      }
    }
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100vw",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 9999,
          background: "rgba(255,255,255,0.5)",
        }}
      >
        <ProgressSpinner />
      </div>
    );
  }

  if (shouldRedirectToLoginError) {
    return (
      <Navigate
        to="/login-error"
        replace
      />
    );
  }

  if (shouldRedirectToLogin) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location }}
      />
    );
  }

  if (
    !isAuthenticated &&
    authType !== "siteminder"
  ) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location }}
      />
    );
  }

  if (
    authType === "siteminder" &&
    sessionStorage.getItem("loginDenied") ===
      "true"
  ) {
    return (
      <Navigate
        to="/login-error"
        replace
      />
    );
  }

  if (
    authType === "siteminder" &&
    !isAuthenticated
  ) {
    console.warn(
      "[PrivateRoute] SiteMinder user is not authenticated. Redirecting to /login."
    );

    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location }}
      />
    );
  }

  if (
    authType === "siteminder" &&
    !isUserValidated
  ) {
    console.log(
      "[PrivateRoute] Waiting for user validation."
    );

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          width: "100%",
        }}
      >
        <ProgressSpinner />
      </div>
    );
  }

  if (
    isAuthenticated &&
    allowedRoles.length > 0 &&
    typeof isAuthorizedUser === "function"
  ) {
    if (!appUser) {
      console.log(
        "[PrivateRoute] appUser not ready yet while checking roles. Waiting."
      );

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
            width: "100%",
          }}
        >
          <ProgressSpinner />
        </div>
      );
    }

    console.log(
      "[PrivateRoute] Checking authorization for roles:",
      allowedRoles,
      "user:",
      appUser
    );

    const authorized =
      isAuthorizedUser(allowedRoles);

    if (!authorized) {
      console.warn(
        "[PrivateRoute] User is not authorized for this route.",
        {
          path: location.pathname,
          allowedRoles,
          appUser,
        }
      );

      if (
        location.pathname !== "/unauthorized"
      ) {
        return (
          <Navigate
            to="/unauthorized"
            replace
          />
        );
      }
    }
  }

  return <>{children}</>;
};

export default PrivateRoute;